/**
 * IANA timezone catalogue.
 *
 * The dropdown used to be backed by `Intl.supportedValuesOf('timeZone')`:
 * every zone the runtime knows, roughly 600 of them, most of which are small
 * towns nobody picks and that make "find mine" a scroll-and-search chore.
 * This is a curated list of major cities instead: one representative per
 * offset in common use, plus the well-known business hubs, so a search for
 * "London" or "+05:30" both land in a handful of results.
 *
 * Each entry carries:
 *   - the canonical IANA id ("America/Los_Angeles")
 *   - the city (split out for display + search)
 *   - the region (continent / ocean, used for grouping)
 *   - the live offset in minutes (computed via Intl, so DST is correct)
 *   - a formatted offset label ("GMT-08:00")
 *
 * The list is sorted by offset (-12 to +14) then alphabetically by city,
 * which is the most natural ordering for "pick a timezone close to mine".
 */

export interface TimezoneEntry {
  id: string
  city: string
  region: string
  offsetMinutes: number
  offsetLabel: string
}

// ── Legacy aliases ───────────────────────────────────────────────────────
// Browsers (Chrome in particular) still hand back these superseded IANA
// names ("Asia/Calcutta" for India rather than "Asia/Kolkata"), so a value
// read straight off `Intl.DateTimeFormat().resolvedOptions().timeZone`, or
// saved on an older meeting, can fail to find itself in the curated list
// below even though the zone is right there under its current name.
const TIMEZONE_ALIASES: Record<string, string> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Europe/Kiev': 'Europe/Kyiv',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Dacca': 'Asia/Dhaka',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'Pacific/Truk': 'Pacific/Chuuk',
  'Asia/Istanbul': 'Europe/Istanbul',
  'Australia/Canberra': 'Australia/Sydney',
  'Asia/Ujung_Pandang': 'Asia/Makassar',
  'US/Eastern': 'America/New_York',
  'US/Central': 'America/Chicago',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'US/Alaska': 'America/Anchorage',
  'US/Hawaii': 'Pacific/Honolulu',
}

const isValidTimeZone = (id: string): boolean => {
  try {
    // eslint-disable-next-line no-new
    new Intl.DateTimeFormat(undefined, { timeZone: id })
    return true
  } catch {
    return false
  }
}

/**
 * The canonical IANA id for a possibly-legacy one. Only maps to an alias
 * target when Intl actually accepts it: an alias table that outruns the
 * runtime's own tz database would trade one broken id for another.
 */
export const canonicalTimezone = (id: string): string => {
  const mapped = TIMEZONE_ALIASES[id]
  if (mapped && isValidTimeZone(mapped)) return mapped
  return id
}

// ── Curated list ─────────────────────────────────────────────────────────
// One entry per offset in everyday use, including the half- and
// quarter-hour ones, plus the major business cities. Validated against the
// runtime's own tz database below, so an id this list is wrong about (an
// older engine that has not caught up with a rename) is dropped rather than
// shown as a dead selection.
const CURATED_TIMEZONE_IDS: string[] = [
  // Pacific / Americas
  'Pacific/Midway', 'Pacific/Honolulu', 'Pacific/Marquesas', 'America/Anchorage',
  'America/Los_Angeles', 'America/Vancouver', 'America/Denver', 'America/Edmonton',
  'America/Phoenix', 'America/Chicago', 'America/Winnipeg', 'America/Mexico_City',
  'America/Bogota', 'America/Lima', 'America/New_York', 'America/Toronto',
  'America/Halifax', 'America/Caracas', 'America/Santiago', 'America/St_Johns',
  'America/Argentina/Buenos_Aires', 'America/Montevideo', 'America/Sao_Paulo',
  'Atlantic/South_Georgia', 'Atlantic/Azores',
  // Europe / Africa
  'UTC', 'Europe/London', 'Europe/Dublin', 'Europe/Lisbon', 'Africa/Casablanca',
  'Africa/Algiers', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome',
  'Europe/Amsterdam', 'Europe/Stockholm', 'Europe/Warsaw', 'Europe/Zurich',
  'Europe/Vienna', 'Europe/Helsinki', 'Europe/Bucharest', 'Europe/Athens',
  'Africa/Cairo', 'Africa/Johannesburg', 'Africa/Lagos', 'Asia/Jerusalem',
  'Europe/Istanbul', 'Africa/Nairobi', 'Europe/Moscow', 'Asia/Baghdad',
  // Middle East / South & Central Asia
  'Asia/Riyadh', 'Asia/Tehran', 'Asia/Dubai', 'Asia/Kabul', 'Asia/Karachi',
  'Asia/Tashkent', 'Asia/Yekaterinburg', 'Asia/Kolkata', 'Asia/Colombo',
  'Asia/Kathmandu', 'Asia/Dhaka', 'Asia/Yangon', 'Asia/Almaty',
  // East / Southeast Asia
  'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Krasnoyarsk', 'Asia/Singapore',
  'Asia/Kuala_Lumpur', 'Asia/Manila', 'Asia/Hong_Kong', 'Asia/Shanghai',
  'Asia/Taipei', 'Asia/Irkutsk', 'Australia/Eucla', 'Asia/Seoul', 'Asia/Tokyo',
  'Asia/Yakutsk',
  // Australia / Pacific
  'Australia/Darwin', 'Australia/Adelaide', 'Australia/Perth', 'Australia/Brisbane',
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Lord_Howe',
  'Asia/Vladivostok', 'Pacific/Guam', 'Pacific/Auckland', 'Pacific/Fiji',
  'Pacific/Noumea', 'Pacific/Chatham', 'Pacific/Tongatapu', 'Pacific/Apia',
  'Pacific/Kiritimati',
]

// `formatToParts` with `timeZoneName: 'shortOffset'` returns strings like
// "GMT", "GMT+5:30", "GMT-8", or sometimes just "UTC". Normalise to minutes.
export const offsetMinutesAt = (timeZone: string, ref: Date = new Date()): number => {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
      hour: '2-digit',
    })
    const part = fmt.formatToParts(ref).find((p) => p.type === 'timeZoneName')
    const raw = part?.value ?? 'GMT'
    if (raw === 'GMT' || raw === 'UTC') return 0
    const match = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(raw)
    if (!match) return 0
    const sign = match[1] === '+' ? 1 : -1
    const hours = parseInt(match[2], 10)
    const minutes = match[3] ? parseInt(match[3], 10) : 0
    return sign * (hours * 60 + minutes)
  } catch {
    return 0
  }
}

const formatOffsetLabel = (minutes: number): string => {
  const sign = minutes < 0 ? '-' : '+'
  const abs = Math.abs(minutes)
  const h = String(Math.floor(abs / 60)).padStart(2, '0')
  const m = String(abs % 60).padStart(2, '0')
  return `GMT${sign}${h}:${m}`
}

const cityFrom = (id: string): string => {
  const tail = id.includes('/') ? id.slice(id.lastIndexOf('/') + 1) : id
  return tail.replace(/_/g, ' ')
}

const regionFrom = (id: string): string => {
  return id.includes('/') ? id.slice(0, id.indexOf('/')) : '-'
}

const buildEntry = (id: string): TimezoneEntry => {
  const offsetMinutes = offsetMinutesAt(id)
  return {
    id,
    city: cityFrom(id),
    region: regionFrom(id),
    offsetMinutes,
    offsetLabel: formatOffsetLabel(offsetMinutes),
  }
}

// Cache the full list once per session: DST recomputation across an open tab
// is an extreme edge case and the alternative (recomputing every offset on
// every keystroke) makes search noticeably laggy.
let cachedTimezones: TimezoneEntry[] | null = null

export const getAllTimezones = (): TimezoneEntry[] => {
  if (cachedTimezones) return cachedTimezones
  const list = CURATED_TIMEZONE_IDS.filter(isValidTimeZone).map(buildEntry)
  list.sort(
    (a, b) =>
      a.offsetMinutes - b.offsetMinutes || a.city.localeCompare(b.city),
  )
  cachedTimezones = list
  return list
}

/** Refresh the cache: call after DST transitions or once an hour for long
 *  sessions if the offset accuracy matters. */
export const refreshTimezoneCache = (): void => {
  cachedTimezones = null
}

export const findTimezone = (id: string): TimezoneEntry | undefined => {
  const canonical = canonicalTimezone(id)
  return getAllTimezones().find((t) => t.id === canonical)
}

/**
 * An entry for any zone Intl accepts, curated or not. For the select's own
 * "make sure the current value is always an option" case: a saved meeting's
 * zone, or the viewer's own, can be a real IANA id that simply isn't one of
 * the curated cities.
 */
export const entryForTimezone = (id: string): TimezoneEntry | undefined => {
  const canonical = canonicalTimezone(id)
  if (!isValidTimeZone(canonical)) return undefined
  return findTimezone(canonical) ?? buildEntry(canonical)
}

export const getDefaultTimezone = (): string => {
  try {
    const id = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    return canonicalTimezone(id)
  } catch {
    return 'UTC'
  }
}

/** Compact label used inside select triggers: "(GMT+05:30) Kolkata". */
export const formatTimezoneLabel = (id: string): string => {
  const tz = findTimezone(id)
  if (tz) return `(${tz.offsetLabel}) ${tz.city}`
  // Not in the curated list (an old saved zone, or one Intl itself does not
  // recognise): still show something useful rather than a bare id.
  const canonical = canonicalTimezone(id)
  if (!isValidTimeZone(canonical)) return id
  const offsetMinutes = offsetMinutesAt(canonical)
  return `(${formatOffsetLabel(offsetMinutes)}) ${cityFrom(canonical)}`
}
